import { useState, useEffect, useMemo } from 'react'
import cx from 'classnames'
import Foco from 'react-foco'
import { Field } from 'formik'
import Styles from '../PropertyForm.module.scss'
import { ReactComponent as Cheveron } from '../../PropertyValue/icons/cheveron-down-small__16.svg'
import { type Values as FormValues } from '../PropertyForm'
import dayjs from 'dayjs'
import { formatDatePropertyValue } from 'common/PropertyValue/utils'

interface Props {
  values: FormValues
  isEdit: boolean
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
}

// NOTE: if you modify this enum, don't forget to also modify its counterpart in
// https://github.com/almanaclabs/almanac-on-rails/blob/master/app/models/property.rb
// TODO: have a single source of truth
export enum DateFormat {
  AllNumeral = 'all_numeral',
  AllNumeralReverse = 'all_numeral_reverse',
  MonthName = 'month_name',
  MonthNameShort = 'month_name_short',
  MonthAndDayName = 'month_and_day_name',
  MonthAndDayNameShort = 'month_and_day_name_short',
  DayOfMonth = 'day_of_month',
  DateTime = 'date_time',
}

const getDateFormats: (currentDate: string) => { [key in DateFormat]: string } = (currentDate: string) => {
  return {
    [DateFormat.AllNumeral]: formatDatePropertyValue(currentDate, DateFormat.AllNumeral),
    [DateFormat.AllNumeralReverse]: formatDatePropertyValue(currentDate, DateFormat.AllNumeralReverse),
    [DateFormat.MonthName]: formatDatePropertyValue(currentDate, DateFormat.MonthName),
    [DateFormat.MonthNameShort]: formatDatePropertyValue(currentDate, DateFormat.MonthNameShort),
    [DateFormat.DayOfMonth]: formatDatePropertyValue(currentDate, DateFormat.DayOfMonth),
    [DateFormat.MonthAndDayName]: formatDatePropertyValue(currentDate, DateFormat.MonthAndDayName),
    [DateFormat.MonthAndDayNameShort]: formatDatePropertyValue(currentDate, DateFormat.MonthAndDayNameShort),
    [DateFormat.DateTime]: formatDatePropertyValue(currentDate, DateFormat.DateTime),
  }
}

const Date = ({ isEdit, setFieldValue, values }: Props) => {
  const {
    meta: { date_format },
  } = values
  const [formatDropdownOpen, setFormatDropdownOpen] = useState(false)
  const [selectedFormat, setSelectedFormat] = useState<DateFormat>(
    date_format && isEdit ? date_format : DateFormat.MonthNameShort,
  )

  const dateFormats = useMemo(() => {
    return getDateFormats(dayjs().toISOString())
  }, [])

  useEffect(() => {
    setFieldValue('meta.date_format', selectedFormat)
  }, [selectedFormat, setFieldValue])

  return (
    <div className={Styles.textSubsection}>
      <div className={Styles.sectionSubheader}>Enter a date or select one from a calendar.</div>
      <div className={Styles.sectionHeader}>Date Format</div>
      <div className={cx(Styles.formSelectContainer, Styles.defaultInputWrapper)}>
        <div onClick={() => setFormatDropdownOpen(true)} className={cx(Styles.formInput, Styles.formSelect)}>
          <Field
            id="date_format"
            type="text"
            name="meta.date_format"
            placeholder="format"
            value={dateFormats[selectedFormat]}
          />
          <Cheveron className={Styles.cheveronIcon} />
        </div>
        {formatDropdownOpen && (
          <Foco onClickOutside={() => setFormatDropdownOpen(false)}>
            <div className={Styles.containerPrompt}>
              <div className={Styles.resultsList}>
                {Object.entries(dateFormats).map(([formatKey, formatValue], index) => (
                  <li
                    className={Styles.resultListItem}
                    onClick={() => {
                      setSelectedFormat(formatKey as DateFormat)
                      setFormatDropdownOpen(false)
                    }}
                    key={index}
                  >
                    {formatValue}
                  </li>
                ))}
              </div>
            </div>
          </Foco>
        )}
      </div>
    </div>
  )
}

export default Date
